import * as THREE from 'three';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { GTAOPass } from 'three/examples/jsm/postprocessing/GTAOPass.js';
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass.js';
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass.js';

/**
 * The finish on the frame: a little occlusion in the corners, a soft glow off
 * anything bright, a colour grade per part of the property, and a fade to black
 * for the moments that should not be a cut.
 *
 * It reads three numbers a frame and changes nothing but pixels. Like the look
 * layer, it never sees the simulation: `grade` is a zone word, `night` a 0..1
 * and `fade` a 0..1, handed in by `main.js`.
 *
 * Cost is what decides how much of it runs. Occlusion is the expensive part,
 * so it is half resolution and only on when the machine has headroom; bloom is
 * half resolution with a high threshold, so only chandeliers, lamps and the
 * sun's glare cross it; the grade and the fade are one cheap full-screen pass.
 * `quality` 'low' drops occlusion, and a headless run on a software rasteriser
 * gets 'low' (see `main.js`), so the measured frame there is the floor and not
 * an excuse.
 */

/** Per zone: a tint multiplied in, a lift added to the shadows, and saturation. */
export const GRADES = Object.freeze({
  boh: { tint: [0.98, 1.0, 1.02], lift: [0.0, 0.004, 0.01], saturation: 0.92 },
  restricted: { tint: [0.97, 1.0, 1.03], lift: [0.0, 0.004, 0.012], saturation: 0.9 },
  foh: { tint: [1.04, 1.0, 0.95], lift: [0.012, 0.006, 0.0], saturation: 1.04 },
  event: { tint: [1.05, 1.0, 0.93], lift: [0.014, 0.007, 0.0], saturation: 1.06 },
  outside: { tint: [1.03, 1.0, 0.96], lift: [0.008, 0.005, 0.002], saturation: 1.05 },
  night: { tint: [0.9, 0.96, 1.1], lift: [0.0, 0.006, 0.02], saturation: 0.82 },
});

const GradeShader = {
  uniforms: {
    tDiffuse: { value: null },
    tint: { value: new THREE.Vector3(1, 1, 1) },
    lift: { value: new THREE.Vector3(0, 0, 0) },
    saturation: { value: 1 },
    vignette: { value: 0.22 },
    fade: { value: 0 },
  },
  vertexShader: /* glsl */ `
    varying vec2 vUv;
    void main() { vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`,
  fragmentShader: /* glsl */ `
    uniform sampler2D tDiffuse;
    uniform vec3 tint;
    uniform vec3 lift;
    uniform float saturation;
    uniform float vignette;
    uniform float fade;
    varying vec2 vUv;
    void main() {
      vec4 c = texture2D(tDiffuse, vUv);
      vec3 col = c.rgb * tint + lift * (1.0 - c.rgb);
      float grey = dot(col, vec3(0.2126, 0.7152, 0.0722));
      col = mix(vec3(grey), col, saturation);
      vec2 d = vUv - 0.5;
      col *= 1.0 - vignette * smoothstep(0.25, 0.75, dot(d, d) * 2.0);
      col *= 1.0 - fade;
      gl_FragColor = vec4(col, c.a);
    }`,
};

export function createPost({ renderer, scene, camera, quality = 'high' }) {
  const size = renderer.getSize(new THREE.Vector2());
  const composer = new EffectComposer(renderer);
  composer.addPass(new RenderPass(scene, camera));

  let ao = null;
  if (quality === 'high') {
    ao = new GTAOPass(scene, camera, Math.max(1, size.x / 2), Math.max(1, size.y / 2));
    ao.output = GTAOPass.OUTPUT.Default;
    ao.blendIntensity = 0.55;
    ao.updateGtaoMaterial({ radius: 0.35, distanceExponent: 1.5, thickness: 1, scale: 1, samples: 8 });
    ao.updatePdMaterial({ lumaPhi: 10, depthPhi: 2, normalPhi: 3, radius: 4, rings: 2, samples: 8 });
    composer.addPass(ao);
  }

  const bloom = new UnrealBloomPass(new THREE.Vector2(size.x / 2, size.y / 2), 0.35, 0.5, 0.86);
  composer.addPass(bloom);

  const grade = new ShaderPass(GradeShader);
  composer.addPass(grade);
  composer.addPass(new OutputPass());

  const current = {
    tint: new THREE.Vector3(1, 1, 1),
    lift: new THREE.Vector3(0, 0, 0),
    saturation: 1,
  };
  const target = { tint: new THREE.Vector3(), lift: new THREE.Vector3() };

  return {
    composer,
    /** Sets where the grade is heading and how dark the screen is, and draws. */
    render({ zone = 'boh', night = 0, fade = 0, dt = 1 / 60 } = {}) {
      const day = GRADES[zone] ?? GRADES.boh;
      const dark = GRADES.night;
      const n = zone === 'outside' ? night : night * 0.3;
      target.tint.set(...day.tint).lerp(new THREE.Vector3(...dark.tint), n);
      target.lift.set(...day.lift).lerp(new THREE.Vector3(...dark.lift), n);
      const sat = day.saturation + (dark.saturation - day.saturation) * n;
      // Eased, so a door is a change of mood and not a cut.
      const ease = 1 - Math.exp(-dt * 3);
      current.tint.lerp(target.tint, ease);
      current.lift.lerp(target.lift, ease);
      current.saturation += (sat - current.saturation) * ease;
      grade.uniforms.tint.value.copy(current.tint);
      grade.uniforms.lift.value.copy(current.lift);
      grade.uniforms.saturation.value = current.saturation;
      grade.uniforms.fade.value = Math.min(1, Math.max(0, fade));
      // The glow is gentler by day: the sun is bright enough already.
      bloom.strength = 0.25 + 0.25 * n;
      composer.render(dt);
    },
    setSize(width, height) {
      composer.setSize(width, height);
      ao?.setSize?.(Math.max(1, width / 2), Math.max(1, height / 2));
      bloom.setSize(width / 2, height / 2);
    },
    get quality() {
      return ao?.enabled !== false && ao ? 'high' : bloom.enabled ? 'low' : 'off';
    },
    /**
     * One step cheaper: occlusion off first, then the glow. The grade is
     * nearly free and stays. Returns whether there was anything left to drop.
     */
    downgrade() {
      if (ao && ao.enabled) {
        ao.enabled = false;
        return true;
      }
      if (bloom.enabled) {
        bloom.enabled = false;
        return true;
      }
      return false;
    },
  };
}
